import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { XCircleIcon, CheckCircleIcon, InformationCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { getApiUrl } from '../config';

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const { register, handleSubmit } = useForm<ForgotPasswordFormData>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [apiStatus, setApiStatus] = useState<'checking' | 'online' | 'offline'>('checking');

  useEffect(() => {
    const checkApiStatus = async () => {
      try {
        await axios.get(`${getApiUrl()}/health`);
        setApiStatus('online');
      } catch (error) {
        setApiStatus('offline');
      }
    };

    checkApiStatus();
  }, []);

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.post(`${getApiUrl()}/auth/forgot-password`, {
        email: data.email,
      });

      let successMessage = 'ສົ່ງລິ້ງກູ້ຄືນລະຫັດຜ່ານແລ້ວ. ກະລຸນາກວດສອບອີເມວຂອງທ່ານ';
      
      // In development mode, show the reset URL
      if (process.env.NODE_ENV === 'development' && response.data.resetUrl) {
        successMessage += `\n\nສຳລັບການພັດທະນາ, ລິ້ງກູ້ຄືນລະຫັດຜ່ານ:\n${response.data.resetUrl}`;
      }
      
      setSuccess(successMessage);
    } catch (err: any) {
      setError(err.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ. ກະລຸນາລອງໃໝ່ອີກຄັ້ງ');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 py-6 sm:py-8 lg:py-12 px-3 sm:px-4 lg:px-6 xl:px-8">
      <div className="max-w-sm sm:max-w-md w-full space-y-6 sm:space-y-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-10 border border-indigo-100 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-50 to-indigo-50 rounded-full -ml-12 -mb-12 opacity-50"></div>
          
          <div className="relative z-10">
            <div className="flex justify-center mb-6">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full p-4 shadow-lg transform hover:scale-105 transition-transform duration-200">
                <EnvelopeIcon className="h-10 w-10 sm:h-12 sm:w-12 text-white" />
              </div>
            </div>
            <h2 className="text-center text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              ລືມລະຫັດຜ່ານ?
            </h2>
            <p className="text-center text-sm sm:text-base text-gray-600 mb-8 leading-relaxed">
              ປ້ອນທີ່ຢູ່ອີເມວຂອງທ່ານ ແລະ ພວກເຮົາຈະສົ່ງລິ້ງກູ້ຄືນລະຫັດຜ່ານໃຫ້ທ່ານ
            </p>

            {apiStatus === 'checking' && (
              <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-semibold text-blue-800 mb-1">
                      ກຳລັງກວດສອບການເຊື່ອມຕໍ່...
                    </h3>
                    <div className="text-sm text-blue-700">
                      <p>ກຳລັງກວດສອບການເຊື່ອມຕໍ່ກັບເຊີເວີ</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {apiStatus === 'offline' && (
              <div className="mb-6 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-semibold text-red-800 mb-1">
                      ບໍ່ສາມາດເຊື່ອມຕໍ່ກັບເຊີເວີໄດ້
                    </h3>
                    <div className="text-sm text-red-700">
                      <p>ກະລຸນາກວດສອບການເຊື່ອມຕໍ່ອິນເຕີເນັດຂອງທ່ານ</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {apiStatus === 'online' && (
              <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <div className="relative">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-3">
                    ທີ່ຢູ່ອີເມວ <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      {...register('email')}
                      className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-sm shadow-sm"
                      placeholder="ປ້ອນທີ່ຢູ່ອີເມວຂອງທ່ານ"
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="group relative w-full flex justify-center items-center py-4 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        ກຳລັງສົ່ງ...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        ສົ່ງລິ້ງກູ້ຄືນລະຫັດຜ່ານ
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {error && (
              <div className="mt-6 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-5">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-red-800 mb-1">
                      ຂໍ້ຜິດພາດ
                    </h3>
                    <div className="text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {success && (
              <div className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-green-800 mb-1">
                      ສຳເລັດ
                    </h3>
                    <div className="text-sm text-green-700">
                      <p className="whitespace-pre-line">{success}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500 font-medium">
                    ຫຼື
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <Link
                  to="/login"
                  className="w-full flex justify-center items-center py-3 px-6 border-2 border-gray-200 hover:border-indigo-300 text-gray-700 hover:text-indigo-600 font-medium rounded-xl transition-all duration-200 text-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  ກັບຄືນໄປໜ້າເຂົ້າສູ່ລະບົບ
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword; 